import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { HexagonBg } from './HexagonBg';
interface MockupFormProps {
  openPrivacy: () => void;
  openTerms: () => void;
}
export function MockupForm({
  openPrivacy,
  openTerms
}: MockupFormProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [consent, setConsent] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!consent) {
      setShowConsentError(true);
      return;
    }
    setShowConsentError(false);
    setStatus('sending');
    const form = e.currentTarget;
    const formData = new FormData(form);
    try {
      formData.append('_subject', 'Solicitud de mockup gratis — Arvex Holding LLC');
      formData.append('_template', 'table');
      formData.append('_captcha', 'false');
      const res = await fetch('https://formsubmit.co/ajax/arvexholdingllc@gmail.com', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };
  return <section id="mockup" className="bg-brand-bg-subtle py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-167">
      <HexagonBg data-id="element-168" />
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-brand-blue-primary/5 rounded-full blur-3xl -translate-y-1/3 -translate-x-1/3 pointer-events-none" data-id="element-169" />

      <div className="max-w-2xl mx-auto relative z-10" data-id="element-170">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} className="text-center mb-10" data-id="element-171">
          <h2 className="font-display font-extrabold text-[clamp(1.8rem,4vw,2.8rem)] text-brand-navy-dark mb-4 leading-tight" data-id="element-172">
            Recibe un mockup gratis de tu{' '}
            <span className="bg-gradient-brand bg-clip-text text-transparent" data-id="element-173">
              página web
            </span>
          </h2>
          <p className="text-brand-text-secondary text-base md:text-lg max-w-lg mx-auto" data-id="element-174">
            Déjanos tus datos y un asesor de Arvex Holding LLC te contactará con
            una propuesta personalizada.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} className="bg-white border border-brand-blue-primary/10 rounded-[20px] p-8 md:p-10 shadow-lg relative overflow-hidden" data-id="element-175">
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-blue-primary via-brand-blue-bright to-brand-gold-champagne" data-id="element-176" />

          {status === 'success' ? <div className="text-center py-8" data-id="element-177">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center" data-id="element-178">
                <CheckCircle2 className="w-9 h-9 text-green-600" data-id="element-179" />
              </div>
              <p className="font-display font-bold text-xl text-brand-navy-dark" data-id="element-180">
                ¡Listo! Un asesor de Arvex Holding LLC te contactará pronto por
                SMS.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-181">
              <div data-id="element-182">
                <label htmlFor="mf-name" className="block text-sm font-semibold text-brand-navy-dark mb-1.5" data-id="element-183">
                  Nombre completo <span className="text-red-500" data-id="element-184">*</span>
                </label>
                <input id="mf-name" required type="text" name="nombre" className="w-full px-4 py-3 rounded-lg border border-brand-blue-primary/15 focus:outline-none focus:ring-2 focus:ring-brand-blue-primary/50 text-sm" data-id="element-185" />
              </div>

              <div data-id="element-186">
                <label htmlFor="mf-business" className="block text-sm font-semibold text-brand-navy-dark mb-1.5" data-id="element-187">
                  Nombre del negocio <span className="text-red-500" data-id="element-188">*</span>
                </label>
                <input id="mf-business" required type="text" name="negocio" className="w-full px-4 py-3 rounded-lg border border-brand-blue-primary/15 focus:outline-none focus:ring-2 focus:ring-brand-blue-primary/50 text-sm" data-id="element-189" />
              </div>

              <div data-id="element-190">
                <label htmlFor="mf-phone" className="block text-sm font-semibold text-brand-navy-dark mb-1.5" data-id="element-191">
                  Número de teléfono <span className="text-red-500" data-id="element-192">*</span>
                </label>
                <input id="mf-phone" required type="tel" name="telefono" placeholder="(305) 555-0123" className="w-full px-4 py-3 rounded-lg border border-brand-blue-primary/15 focus:outline-none focus:ring-2 focus:ring-brand-blue-primary/50 text-sm" data-id="element-193" />
              </div>

              <div data-id="element-194">
                <label htmlFor="mf-email" className="block text-sm font-semibold text-brand-navy-dark mb-1.5" data-id="element-195">
                  Correo electrónico{' '}
                  <span className="text-brand-text-muted font-normal" data-id="element-196">
                    (opcional)
                  </span>
                </label>
                <input id="mf-email" type="email" name="email" className="w-full px-4 py-3 rounded-lg border border-brand-blue-primary/15 focus:outline-none focus:ring-2 focus:ring-brand-blue-primary/50 text-sm" data-id="element-197" />
              </div>

              <div data-id="element-198">
                <label className="flex items-start space-x-3 cursor-pointer" data-id="element-199">
                  <input type="checkbox" name="consentimiento" checked={consent} onChange={e => {
                setConsent(e.target.checked);
                if (e.target.checked) setShowConsentError(false);
              }} className="mt-1 w-4 h-4 shrink-0 accent-brand-blue-primary rounded border-brand-blue-primary/30" data-id="element-200" />
                  <span className="text-xs text-brand-text-secondary leading-relaxed" data-id="element-201">
                    Acepto recibir mensajes de texto (SMS) de Arvex Holding LLC
                    sobre mi solicitud. La frecuencia de los mensajes puede
                    variar. Aplican tarifas de mensajes y datos. Responde STOP
                    para cancelar en cualquier momento. Ver{' '}
                    <button type="button" onClick={openPrivacy} className="text-brand-blue-primary underline hover:text-brand-blue-electric" data-id="element-202">
                      Política de Privacidad
                    </button>{' '}
                    y{' '}
                    <button type="button" onClick={openTerms} className="text-brand-blue-primary underline hover:text-brand-blue-electric" data-id="element-203">
                      Términos y Condiciones
                    </button>
                    .
                  </span>
                </label>
                {showConsentError && <p className="text-red-500 text-xs font-medium mt-2 ml-7" data-id="element-204">
                    Debes aceptar para continuar.
                  </p>}
              </div>

              {status === 'error' && <p className="text-red-500 text-sm font-medium" data-id="element-205">
                  Hubo un error al enviar. Por favor inténtalo de nuevo.
                </p>}

              <button type="submit" disabled={status === 'sending'} className="w-full bg-gradient-button text-white py-4 rounded-xl font-bold shadow-[0_4px_20px_rgba(30,95,212,0.3)] hover:shadow-[0_6px_28px_rgba(30,95,212,0.4)] transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0" data-id="element-206">
                {status === 'sending' ? 'Enviando...' : 'Solicitar mi mockup gratis'}
              </button>
            </form>}
        </motion.div>
      </div>
    </section>;
}